<template>
  <div id="app" class="app-container">
    <!-- 顶部导航 -->
    <el-header v-if="!isFullscreen" class="app-header" height="60px">
      <div class="header-content">
        <div class="header-left">
          <!-- 移动端菜单按钮 -->
          <el-button 
            v-if="isMobile" 
            :icon="Fold" 
            @click="toggleSidebar"
            class="mobile-menu-btn"
            circle
            size="small"
          />
          <h1 class="app-title">
            🚄 ROFC车站管理系统
          </h1>
          <el-divider v-if="!isMobile" direction="vertical" class="header-divider"/>
          <span v-if="!isMobile" class="app-subtitle">Railway Operations & Fleet Control</span>
        </div>
        <div class="header-right">
          <el-badge :value="statsData.total_stations" :max="999999" class="stats-badge" type="success">
            <el-button size="small" class="stats-icon-button stats-icon-button--stations" :icon="LocationInformation" circle />
          </el-badge>
          <el-badge :value="statsData.total_lines" :max="999999" class="stats-badge" type="warning">
            <el-button size="small" class="stats-icon-button stats-icon-button--lines" :icon="Connection" circle />
          </el-badge>
          <el-tooltip content="系统状态" placement="bottom">
            <el-tag
              :type="systemStatus.type"
              size="small"
              class="header-status-tag"
              @click="triggerHiddenDebug"
            >
              {{ systemStatus.text }}
            </el-tag>
          </el-tooltip>
          
          <!-- 用户信息 -->
          <el-dropdown @command="handleUserCommand" trigger="click">
            <div class="user-info">
              <el-avatar :size="32" :src="userAvatar">
                {{ userInitials }}
              </el-avatar>
              <span class="username" v-if="!isMobile">{{ user?.username }}</span>
              <el-icon class="dropdown-icon" v-if="!isMobile"><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile" v-if="!isGuest">
                  <el-icon><User /></el-icon>
                  个人资料
                </el-dropdown-item>
                <el-dropdown-item command="password" v-if="!isGuest">
                  <el-icon><Lock /></el-icon>
                  修改密码
                </el-dropdown-item>
                <el-dropdown-item v-if="!isGuest && isAdmin" command="users" divided>
                  <el-icon><UserFilled /></el-icon>
                  用户管理
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided>
                  <el-icon><SwitchButton /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </el-header>

    <!-- 主体内容 -->
    <el-container class="app-main">
      <!-- 侧边栏导航 -->
      <el-aside v-if="!isFullscreen"
        class="app-sidebar" 
        :class="{ 'mobile-sidebar': isMobile, 'sidebar-collapsed': isMobile && !sidebarVisible }"
        :width="isMobile ? '100%' : '280px'"
      >
        <el-menu
          :default-active="$route.path"
          router
          class="sidebar-menu"
          background-color="#ffffff"
          text-color="#303133"
          active-text-color="#ff7a45"
        >
          <template v-for="route in menuRoutes" :key="route.path">
            <el-menu-item
              :index="route.name === 'BlueMap' ? $route.path : route.path"
              v-if="route.meta"
              @click="handleMenuClick(route)"
              :class="{ 'no-bg-menu-item': route.name === 'BlueMap' || route.name === 'MapView' }"
            >
              <div class="menu-item-content" :class="{ 'has-inline-action': route.name === 'BlueMap' }">
                <div class="menu-item-main">
                  <el-icon><component :is="route.meta.icon" /></el-icon>
                  <span>{{ route.meta.title }}</span>
                  <el-tag
                    v-if="route.meta.new"
                    size="small"
                    type="success"
                    effect="dark"
                    style="margin-left:8px;"
                  >NEW</el-tag>
                </div>
                <el-tooltip
                  v-if="route.name === 'BlueMap'"
                  content="在当前页面打开 BlueMap"
                  placement="top"
                >
                  <button
                    class="menu-inline-action"
                    type="button"
                    @click.stop="openMapOverlay"
                  >
                    <el-icon style="margin-left: 4px;"><Monitor /></el-icon>
                  </button>
                </el-tooltip>
              </div>
            </el-menu-item>
          </template>
        </el-menu>

        <!-- 快捷操作 -->
        <div v-if="!isGuest" class="sidebar-footer">
          <el-button
            type="primary"
            :icon="Plus"
            @click="showQuickAdd = true"
            class="quick-add-btn"
          >
            快速添加车站
          </el-button>
        </div>
      </el-aside>

      <!-- 移动端遮罩层 -->
      <div 
        v-if="isMobile && sidebarVisible" 
        class="sidebar-overlay"
        @click="closeSidebar"
      ></div>

      <!-- 主内容区域 -->
      <el-main class="app-content" :class="{ 'mobile-content': isMobile, 'fullscreen': isFullscreen }">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <keep-alive include="StationNavigator">
              <component :is="Component" @refresh-stats="loadStats" />
            </keep-alive>
          </transition>
        </router-view>
        
        <!-- ICP备案号 -->
        <div v-if="!isFullscreen" class="icp-footer">
          <a href="http://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
            京ICP备2025148253号
          </a>
        </div>
      </el-main>
    </el-container>

        <!-- 快速添加车站对话框 -->
        <el-dialog
            v-if="!isGuest"
            v-model="showQuickAdd"
            title="快速添加车站"
            width="500px"
            draggable
        >
            <QuickAddStation @success="handleQuickAddSuccess" @cancel="showQuickAdd = false" />
        </el-dialog>

    <BlueMapOverlay
      ref="blueMapOverlayRef"
      v-model="showBlueMapOverlay"
      @station-saved="handleOverlaySave"
      @building-saved="handleOverlaySave"
    />

    <McpDebugConsole v-if="!isFullscreen" v-model="showMcpDebug" :show-fab="false" />
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  LocationInformation,
  Connection,
  Download,
  Plus,
  DataAnalysis,
  Fold,
  User,
  Lock,
  UserFilled,
  SwitchButton,
  ArrowDown,
  Monitor
} from '@element-plus/icons-vue'
import { exportApi } from './api'
import QuickAddStation from './components/QuickAddStation.vue'
import { useAuth } from './composables/useAuth'
import BlueMapOverlay from './components/BlueMapOverlay.vue'
import McpDebugConsole from './components/McpDebugConsole.vue'

export default {
  name: 'App',
  components: {
    QuickAddStation,
    BlueMapOverlay,
    McpDebugConsole
  },
  setup() {
    const router = useRouter()
    const showQuickAdd = ref(false)
    const showMcpDebug = ref(false)
    const showBlueMapOverlay = ref(false)
    const blueMapOverlayRef = ref(null)
    // 是否为全屏路由
    const isFullscreen = computed(() => {
      const matched = router.currentRoute.value
      return matched?.meta?.fullscreen === true
    })
    
    // 认证状态
    const { user, isAdmin, isGuest, logout, initAuth, hasPermission } = useAuth()
    onMounted(() => {
      initAuth()
    })

    // 移动端检测
    const isMobile = ref(false)
    const sidebarVisible = ref(false)
    
    // 统计数据
    const statsData = reactive({
      total_stations: 0,
      total_lines: 0,
      transfer_stations: 0
    })
    
    // 系统状态
    const systemStatus = computed(() => {
      if (statsData.total_stations === 0) {
        return { type: 'info', text: '待初始化' }
      } else if (statsData.total_stations < 10) {
        return { type: 'warning', text: '数据较少' }
      } else {
        return { type: 'success', text: '运行正常' }
      }
    })

    const debugClickCount = ref(0)
    const debugClickTimer = ref(null)
    const triggerHiddenDebug = () => {
      debugClickCount.value += 1
      if (debugClickTimer.value) {
        clearTimeout(debugClickTimer.value)
      }
      debugClickTimer.value = setTimeout(() => {
        debugClickCount.value = 0
        debugClickTimer.value = null
      }, 600)

      if (debugClickCount.value >= 3) {
        debugClickCount.value = 0
        if (debugClickTimer.value) {
          clearTimeout(debugClickTimer.value)
          debugClickTimer.value = null
        }
        showMcpDebug.value = true
        ElMessage.success('已打开 AI 调试面板')
      }
    }
    
    // 菜单路由
    const menuRoutes = computed(() => {
      return router.getRoutes().filter(route => {
        if (!route.meta || !route.meta.title || route.meta.hidden) {
          return false
        }
        if (isGuest.value && !route.meta.allowGuest) {
          return false
        }
        if (route.meta?.requiredPermissions && !hasPermission(route.meta.requiredPermissions)) {
          return false
        }
        return true
      })
    })
    
    // 加载统计数据
    const loadStats = async () => {
      if (isGuest.value) return
      try {
        const result = await exportApi.getStats()
        Object.assign(statsData, result.data)
      } catch (error) {
        console.error('加载统计数据失败:', error)
      }
    }
    
    // 快速导出
    const quickExport = async () => {
      if (isGuest.value) {
        ElMessage.warning('访客模式不支持导出操作')
        return
      }
      try {
        await exportApi.exportBlueMap()
        ElMessage.success('配置文件导出成功')
      } catch (error) {
        ElMessage.error('导出失败: ' + error.message)
      }
    }
    
    // 快速添加成功
    const handleQuickAddSuccess = () => {
      showQuickAdd.value = false
      loadStats()
      ElMessage.success('车站添加成功')
    }
    
    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768
      if (!isMobile.value) {
        sidebarVisible.value = false
      }
    }
    
    // 切换侧边栏
    const toggleSidebar = () => {
      sidebarVisible.value = !sidebarVisible.value
    }
    
    // 关闭侧边栏
    const closeSidebar = () => {
      sidebarVisible.value = false
    }
    
    // 菜单点击处理
    const handleMenuClick = (route) => {
      if (route?.name === 'BlueMap') {
        window.open('https://map.rofc.online/', '_blank', 'noopener,noreferrer')
        return
      }
      if (isMobile.value) {
        closeSidebar()
      }
    }

    const openMapOverlay = () => {
      // 如果地图已经打开但处于收起状态，则恢复地图
      if (showBlueMapOverlay.value && blueMapOverlayRef.value?.collapsed) {
        blueMapOverlayRef.value.restoreMap()
      } else {
        // 否则正常打开地图
        showBlueMapOverlay.value = true
      }
    }

    const handleOverlaySave = () => {
      if (!isGuest.value) {
        loadStats()
      }
    }
    
    // 用户头像和姓名首字母
    const userAvatar = computed(() => {
      return null // 可以后续添加头像上传功能
    })
    
    const userInitials = computed(() => {
      if (user.value?.username) {
        return user.value.username.charAt(0).toUpperCase()
      }
      return 'U'
    })
    
    // 用户下拉菜单处理
    const handleUserCommand = (command) => {
      switch (command) {
        case 'profile':
          ElMessage.info('个人资料功能开发中')
          break
        case 'password':
          ElMessage.info('修改密码功能开发中')
          break
        case 'users':
          ElMessage.info('用户管理功能开发中')
          break
        case 'logout':
          logout()
          break
      }
    }
    
    onMounted(() => {
      initAuth()
      if (!isGuest.value) {
        loadStats()
      }
      checkMobile()
      window.addEventListener('resize', checkMobile)
    })
    
    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile)
      if (debugClickTimer.value) {
        clearTimeout(debugClickTimer.value)
      }
    })
    
    return {
      showQuickAdd,
      showMcpDebug,
      isMobile,
      sidebarVisible,
      statsData,
      systemStatus,
      triggerHiddenDebug,
      menuRoutes,
      user,
      isGuest,
      isAdmin,
      userAvatar,
      userInitials,
      isFullscreen,
      loadStats,
      quickExport,
      handleQuickAddSuccess,
      toggleSidebar,
      closeSidebar,
      handleMenuClick,
      openMapOverlay,
      handleOverlaySave,
      handleUserCommand,
      blueMapOverlayRef,
      // Icons
      LocationInformation,
      Connection,
      Download,
      Plus,
      DataAnalysis,
      Fold,
      User,
      Lock,
      UserFilled,
      SwitchButton,
      ArrowDown,
      Monitor,
      showBlueMapOverlay
    }
  }
}
</script>

<style scoped>
/* 头部样式 */
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  gap: 8px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.app-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  white-space: nowrap;
}

.header-divider {
  margin: 0 20px;
  border-color: rgba(255, 255, 255, 0.3);
}

.app-subtitle {
  opacity: 0.8;
  white-space: nowrap;
}

.mobile-menu-btn {
  margin-right: 8px;
}

.stats-badge {
  margin-right: 8px;
}

.stats-icon-button {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 246, 236, 0.95) 100%);
  color: var(--brand-primary);
  border: none;
  box-shadow: 0 12px 26px rgba(255, 122, 69, 0.24);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stats-icon-button--lines {
  color: var(--brand-accent);
  box-shadow: 0 12px 26px rgba(255, 111, 97, 0.22);
}

.stats-icon-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px rgba(255, 122, 69, 0.32);
}

.stats-icon-button--lines:hover {
  box-shadow: 0 16px 30px rgba(255, 111, 97, 0.32);
}

/* 用户信息样式 */
.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.user-info:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.username {
  font-size: 14px;
  font-weight: 500;
  color: white;
}

.dropdown-icon {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

/* 侧边栏样式 */
.sidebar-menu {
  border-right: none;
  height: calc(100vh - 140px);
}

.menu-item-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.menu-item-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.menu-inline-action {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  margin-left: 12px;
  background: var(--brand-primary-soft);
  color: var(--brand-primary-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.menu-inline-action:hover {
  background: rgba(255, 111, 97, 0.18);
  color: var(--brand-accent);
}

.has-inline-action .menu-item-main {
  flex: 1;
}

.sidebar-menu .el-menu-item {
  height: 48px;
  line-height: 48px;
  margin: 4px 8px;
  border-radius: 6px;
}

.sidebar-menu .el-menu-item:hover {
  background-color: rgba(255, 152, 85, 0.12);
}

.sidebar-menu .el-menu-item.is-active {
  background-color: rgba(255, 138, 76, 0.18);
  color: var(--brand-primary);
}

.sidebar-menu .el-menu-item.no-bg-menu-item {
  background-color: transparent !important;
}

.sidebar-menu .el-menu-item.no-bg-menu-item:hover {
  background-color: rgba(255, 152, 85, 0.12) !important;
}

.sidebar-menu .el-menu-item.no-bg-menu-item.is-active {
  background-color: transparent !important;
}

.sidebar-footer {
  padding: 16px;
  border-top: 1px solid rgba(255, 165, 120, 0.3);
  margin-top: auto;
}

.quick-add-btn {
  width: 100%;
}

.app-header {
  position: relative;
  z-index: 1000;
}

.app-sidebar {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  transition: transform 0.3s ease;
}

/* 移动端样式 */
@media (max-width: 768px) {
  .header-content {
    flex-wrap: nowrap;
    gap: 6px;
    align-items: center;
  }

  .header-left {
    flex: 1;
    min-width: 0;
    flex-wrap: nowrap;
    align-items: center;
  }

  .header-right {
    flex-shrink: 0;
    flex-wrap: nowrap;
    justify-content: flex-end;
    min-width: 0;
    gap: 6px;
  }

  .user-info {
    width: auto;
    justify-content: center;
    padding: 2px 4px;
  }

  .app-title {
    font-size: 15px;
  }
  
  .stats-badge {
    margin-right: 0;
  }
  
  .stats-icon-button {
    transform: scale(0.85);
    box-shadow: 0 6px 14px rgba(255, 122, 69, 0.18);
  }
  
  .header-status-tag {
    transform: scale(0.85);
  }
  
  /* 移动端侧边栏 */
  .mobile-sidebar {
    position: fixed;
    top: 60px;
    left: 0;
    z-index: 999;
    transform: translateX(-100%);
    width: 280px !important;
    height: calc(100vh - 60px);
    background: white;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  }
  
  .mobile-sidebar:not(.sidebar-collapsed) {
    transform: translateX(0);
  }
  
  .sidebar-overlay {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 998;
  }
  
  .mobile-content {
    padding: 12px;
  }
  
  /* 移动端对话框 */
  :deep(.el-dialog) {
    width: 95% !important;
    margin: 0 auto !important;
  }
}

/* 小屏幕优化 */
@media (max-width: 480px) {
  .app-title {
    font-size: 14px;
  }
  
  .header-right {
    gap: 4px;
  }
  
  .user-info {
    padding: 2px 4px;
  }
  
  .mobile-content {
    padding: 8px;
  }
}

/* ICP备案号样式 */
.app-content {
  position: relative;
}

.icp-footer {
  position: fixed;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  text-align: center;
  pointer-events: none;
}

.icp-footer a {
  color: #909399;
  font-size: 12px;
  text-decoration: none;
  transition: color 0.2s;
  pointer-events: auto;
  display: inline-block;
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 4px;
  backdrop-filter: blur(4px);
}

.icp-footer a:hover {
  color: var(--brand-primary);
  background: rgba(255, 255, 255, 0.9);
}
</style>
